/**
 * useCreativePipeline — React Query hooks dla nowego backendu kanbana.
 * Backed by server/modules/creative-pipeline/routes.ts
 */
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { CreativePipelineTask } from "@shared/schema";

export interface PipelineColumn {
  id: string;
  tasks: CreativePipelineTask[];
}

const KEY = (agencyClientId?: string | null) =>
  ["creative-pipeline", agencyClientId ?? "global"] as const;

export function useCreativePipeline(agencyClientId?: string | null) {
  return useQuery<{ columns: PipelineColumn[] }>({
    queryKey: KEY(agencyClientId),
    queryFn: () => {
      const qs = agencyClientId ? `?agencyClientId=${agencyClientId}` : "";
      return apiRequest("GET", `/api/creative-pipeline${qs}`);
    },
    refetchInterval: 30_000,
  });
}

export function useMoveTask(agencyClientId?: string | null) {
  const key = KEY(agencyClientId ?? null);
  return useMutation({
    mutationFn: ({ id, columnId, position }: { id: string; columnId: string; position: number }) =>
      apiRequest("POST", `/api/creative-pipeline/tasks/${id}/move`, { columnId, position }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creative-pipeline"] });
    },
  });
}

export interface CreateTaskInput {
  agencyClientId?: string | null;
  columnId: string;
  sourceType?: string;
  title: string;
  description?: string | null;
  mediaType?: "image" | "video" | "text" | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  metadata?: Record<string, unknown> | null;
  autopilotSessionId?: string | null;
}

export function useCreateTask(agencyClientId?: string | null) {
  return useMutation({
    mutationFn: (input: CreateTaskInput) =>
      apiRequest("POST", "/api/creative-pipeline/tasks", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(agencyClientId ?? null) });
    },
  });
}

export function useArchiveTask(agencyClientId?: string | null) {
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/creative-pipeline/tasks/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY(agencyClientId ?? null) }),
  });
}

export function useGenerateAssetForTask(agencyClientId?: string | null) {
  return useMutation({
    mutationFn: (taskId: string) =>
      apiRequest("POST", `/api/creative-pipeline/tasks/${taskId}/generate-asset`) as Promise<{
        ok: boolean;
        mediaUrl?: string;
        error?: string;
      }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(agencyClientId ?? null) });
    },
  });
}

export interface UploadContextResponse {
  hasMetaAccount: boolean;
  hasMetaPage: boolean;
  hasWinner: boolean;
  metric: string;
  topAdsets: Array<{
    adsetId: string;
    spend: number;
    conversions: number;
    cpa: number | null;
    roas: number | null;
    rankValue: number | null;
  }>;
  recommendation: "duplicate_winner" | "propose_existing" | "ask_user_to_create";
  warning?: string;
}

export function useUploadContext(sessionId: string | null) {
  return useQuery<UploadContextResponse>({
    queryKey: ["autopilot-upload-context", sessionId],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/upload-context`),
    enabled: !!sessionId,
    staleTime: 30_000,
  });
}

export interface UploadToMetaInput {
  mode: "duplicate_winner" | "duplicate_specific" | "add_to_existing" | "create_new";
  targetAdsetId?: string;
  status: "PAUSED" | "ACTIVE";
  newAdSetParams?: {
    campaignId: string;
    audienceId?: string | null;
    dailyBudgetPLN: number;
    landingPageUrl: string;
    geoCountry?: string;
  };
}

export interface CampaignsAndAudiencesResponse {
  campaigns: Array<{ id: string; name: string; status: string; objective: string }>;
  audiences: Array<{ id: string; name: string; subtype: string }>;
  error?: string;
}

export function useCampaignsAndAudiences(sessionId: string | null) {
  return useQuery<CampaignsAndAudiencesResponse>({
    queryKey: ["autopilot-campaigns-audiences", sessionId],
    queryFn: () =>
      apiRequest("GET", `/api/autopilot/sessions/${sessionId}/campaigns-and-audiences`),
    enabled: !!sessionId,
    staleTime: 60_000,
  });
}

export function useUploadTaskToMeta(agencyClientId?: string | null) {
  return useMutation({
    mutationFn: ({ taskId, options }: { taskId: string; options: UploadToMetaInput }) =>
      apiRequest(
        "POST",
        `/api/creative-pipeline/tasks/${taskId}/upload-to-meta`,
        options
      ) as Promise<{ ok: boolean; metaAdId?: string; metaAdsetId?: string; error?: string }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(agencyClientId ?? null) });
    },
  });
}
